import type { FinanceCarryReport } from "@chro/shared";
import { defaultFinanceMonth } from "./finance-report";

/** Limit replay work and use the same completed-month calendar as Finance. */
export function financeCarryRangeError(from: string, to: string, now = new Date()): string | null {
  const valid = (value: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(value) && value >= "2000-01";
  if (!valid(from) || !valid(to)) return "Choose a valid from month and to month.";
  if (from > to) return "The from month must be no later than the to month.";
  if (to > defaultFinanceMonth(now)) return "Choose completed financial months only.";
  const index = (value: string) => Number(value.slice(0, 4)) * 12 + Number(value.slice(5));
  if (index(to) - index(from) >= 24) return "Choose a range of no more than 24 months.";
  return null;
}

export function financeCarryPath(from: string, to: string, version?: string): string {
  const query = new URLSearchParams({ from, to });
  if (version !== undefined) query.set("version", version);
  return `/eex/recognition/finance-report/carry${version === undefined ? "" : ".xlsx"}?${query}`;
}

export function assertFinanceCarryScope(report: FinanceCarryReport, companyId: string | null, from: string, to: string): void {
  if ((companyId && report.companyId !== companyId) || report.from !== from || report.to !== to) {
    throw new Error("The returned report does not match the selected company and month range.");
  }
  const months: string[] = [];
  for (let date = new Date(`${from}-01T00:00:00Z`); date.toISOString().slice(0, 7) <= to; date.setUTCMonth(date.getUTCMonth() + 1)) {
    months.push(date.toISOString().slice(0, 7));
  }
  if (report.months.length !== months.length || report.months.some((month, index) => month.month !== months[index])) {
    throw new Error("The returned report does not contain every selected month in order.");
  }
}

export function financeCarryDetailTotal(amounts: Array<number | null>): number | null {
  if (amounts.some((amount) => amount == null)) return null;
  return Math.round(amounts.reduce<number>((sum, amount) => sum + amount!, 0) * 100) / 100;
}
